import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { PaCompactMetricChip, PaEmptyState, PaSkeleton } from "components/platform-admin";
import {
  buildCatalogMetricChips,
  buildUsageMetricChips,
} from "./platformDashboardCapabilities.utils";
import { buildAiUsageDashboardChips } from "../AiUsage/aiUsageOverview.utils";

const WidgetRow = ({ title, action, children }) => {
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return null;
  }

  return (
    <div className="mb-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</p>
        {action}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
};

export const PlatformDashboardCatalogSummary = ({ catalog, loading }) => {
  if (loading) {
    return (
      <WidgetRow title="Catalog">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="h-[52px] min-w-[7rem] flex-1 animate-pulse rounded-lg border border-gray-200 bg-gray-100"
          />
        ))}
      </WidgetRow>
    );
  }

  const chips = buildCatalogMetricChips(catalog);

  return (
    <WidgetRow title="Catalog">
      {chips.map((chip) => (
        <PaCompactMetricChip key={chip.id} {...chip} />
      ))}
    </WidgetRow>
  );
};

export const PlatformDashboardUsageSummary = ({ usage, loading }) => {
  if (loading) {
    return (
      <WidgetRow title="Feature usage">
        <div className="h-[52px] min-w-[7rem] flex-1 animate-pulse rounded-lg border border-gray-200 bg-gray-100" />
      </WidgetRow>
    );
  }

  const chips = buildUsageMetricChips(usage);

  return (
    <WidgetRow
      title="Feature usage"
      action={
        <Link to="/usage" className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
          Usage heatmap →
        </Link>
      }
    >
      {chips.map((chip) => (
        <PaCompactMetricChip key={chip.id} {...chip} />
      ))}
    </WidgetRow>
  );
};

export const PlatformDashboardAiUsageSummary = ({ aiUsage, loading }) => {
  if (loading) {
    return (
      <WidgetRow title="AI usage">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="h-[52px] min-w-[7rem] flex-1 animate-pulse rounded-lg border border-gray-200 bg-gray-100"
          />
        ))}
      </WidgetRow>
    );
  }

  const chips = buildAiUsageDashboardChips(aiUsage);

  return (
    <WidgetRow
      title="AI usage"
      action={
        <Link to="/ai-usage" className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
          AI overview →
        </Link>
      }
    >
      {chips.map((chip) => (
        <PaCompactMetricChip key={chip.id} {...chip} />
      ))}
    </WidgetRow>
  );
};

export const PlatformDashboardOperationsPanel = ({ items, loading }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-sm font-semibold text-gray-900">Suspended tenants</h2>
      <Link
        to="/tenants?status=suspended"
        className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
      >
        View all →
      </Link>
    </div>

    {loading ? (
      <PaSkeleton variant="text" lines={4} />
    ) : items.length === 0 ? (
      <PaEmptyState
        title="No suspended tenants"
        message="Suspended service providers appear here for operations review."
      />
    ) : (
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.id}>
            <Link
              to={`/tenants/${item.tenantId}`}
              className={`block border-l-4 ${item.borderClass} rounded-r-lg bg-gray-50 px-3 py-2 transition hover:bg-gray-100`}
            >
              <p className="text-sm font-medium text-gray-900">{item.label}</p>
              {item.detail ? (
                <p className="mt-0.5 text-xs text-gray-500">{item.detail}</p>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export const formatUsageRefreshedLabel = (value) => {
  if (!value) {
    return null;
  }

  const timestamp = moment(value);
  return timestamp.isValid() ? `Last refresh ${timestamp.fromNow()}` : null;
};
